import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import { ArchClient } from 'apis/entities/architecture.entity'
import { useRef, useState } from 'react'
import ButtonSpinner from './ButtonSpinner'
import { StyleUtil } from 'utils/StyleUtil'
import { ToastUtil } from 'utils/ToastUtil'
import Svg, { Icon, IconClass } from 'components/Svg'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'

interface WidgetArchManageClientProps {
  clients: ArchClient[]
  onClose: () => void
  onAdded: (client: ArchClient) => void
  onRenamed: (client: ArchClient) => void
}

export default function WidgetArchManageClient({
  clients,
  onClose,
  onAdded,
  onRenamed,
}: WidgetArchManageClientProps) {
  const { updateArchClient, createArchClient } = useArchitectureApi()
  const { getRole } = useCompositeAuth()
  const canCreateClient = getRole() === Role.SuperAdmin
  const refAddClient = useRef<HTMLInputElement>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [isUpdatingIds, setIsUpdatingIds] = useState<string[]>([])
  const [currentClients, setCurrentClients] = useState<ArchClient[]>(
    clients.sort((a, b) => a.name.localeCompare(b.name)),
  )

  const addClient = async () => {
    if (!canCreateClient) {
      ToastUtil.warning('You do not have permission to create clients')
      return
    }

    try {
      setIsAdding(true)
      const clientName = refAddClient.current?.value
      if (!clientName) {
        ToastUtil.warning('Client name is required')
        return
      }
      const client = await createArchClient(clientName)
      onAdded(client)

      const sortedClients = [...currentClients, client].sort((a, b) =>
        a.name.localeCompare(b.name),
      )

      // update the current clients
      setCurrentClients(sortedClients)
      // clear the input text
      refAddClient.current!.value = ''
      ToastUtil.success('Added successfully')
    } catch (error) {
      console.error(error)
      ToastUtil.error('Could not add')
    } finally {
      setIsAdding(false)
    }
  }

  const renameClient = async (clientId: string) => {
    try {
      const clientName = (
        document.getElementById(`client-${clientId}`) as HTMLInputElement
      ).value
      setIsUpdatingIds([...isUpdatingIds, clientId])
      await updateArchClient(clientId, clientName)
      onRenamed({ id: clientId, name: clientName } as ArchClient)
      // update the current clients
      const sortedClients = currentClients
        .map((client) =>
          client.id === clientId ? { ...client, name: clientName } : client,
        )
        .sort((a, b) => a.name.localeCompare(b.name))

      setCurrentClients(sortedClients)
      ToastUtil.success('Renamed successfully')
    } catch (error) {
      console.error(error)
      ToastUtil.error('Could not rename')
    } finally {
      setIsUpdatingIds(isUpdatingIds.filter((id) => id !== clientId))
    }
  }

  // on input text chaange, change the color of the input text
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.style.color = e.target.value ? '#000000' : '#92929d'
  }

  return (
    <div
      data-testid="modal-manage-clients"
      className="absolute z-[999] flex h-full w-full items-center justify-center bg-[#D5D5DBAA]"
    >
      <div className="flex h-auto w-1/2 min-w-[620px] flex-col gap-[12px] bg-white p-[24px] pt-[12px] shadow-xl">
        <div className="flex flex-row">
          <div className="grow" />
          <div
            className="cursor-pointer self-end"
            onClick={onClose}
            data-testid="btn-close-manage-clients"
          >
            <Svg icon={Icon.Close} className={IconClass.FillBlack2} />
          </div>
        </div>
        <div
          className="mentem-scrollbar-2 flex max-h-[70vh] flex-col gap-[12px] overflow-auto pr-1"
          data-testid="clients-list-container"
        >
          {canCreateClient && (
            <div className="flex flex-row items-center gap-[36px]">
              <input
                type="text"
                placeholder="Client name"
                disabled={isAdding}
                ref={refAddClient}
                className="arch-add-client-input-text"
                data-testid="input-add-new-client"
                onChange={onInputChange}
              />
              <button
                data-testid="btn-create-new-client"
                className={`${StyleUtil.buttonPrimaryUnsw} min-w-[130px]`}
                onClick={addClient}
              >
                {isAdding ? <ButtonSpinner /> : 'Create new'}
              </button>
            </div>
          )}
          <div
            data-testid="existing-clients-list"
            className="flex flex-col gap-[12px]"
          >
            {currentClients.map((client) => (
              <div
                key={client.id}
                className="flex flex-row items-center gap-[36px]"
                data-testid={`client-row-${client.id}`}
              >
                <input
                  id={`client-${client.id}`}
                  type="text"
                  defaultValue={client.name}
                  placeholder="Client name"
                  className="arch-client-input-text"
                  data-testid={`input-client-${client.id}`}
                  onChange={onInputChange}
                />
                <button
                  data-testid={`btn-rename-client-${client.id}`}
                  className={`${StyleUtil.buttonBlackWhite} min-w-[130px]`}
                  onClick={() => renameClient(client.id)}
                >
                  {isUpdatingIds.includes(client.id) ? (
                    <ButtonSpinner />
                  ) : (
                    'Rename'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
